import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBullhorn,
  faUsers,
  faUserPlus,
  faShoppingBag,
  faWallet,
  faMobileAlt,
  faPaperPlane,
  faSpinner,
  faCheckCircle,
  faExclamationTriangle,
  faCommentDots,
  faMagic,
  faPhone,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../AuthContext';
import Modal from '../../components/Modal';
import './AdminOffers.css';

const API = process.env.REACT_APP_API_URL || 'https://api.bustisha.com/api';

const TEMPLATES = [
  {
    id: 'deposit_bonus',
    title: '🎁 20% Deposit Bonus',
    badge: 'Popular',
    text: 'Hello {username}, get 20% EXTRA bonus on your next deposit today on Bustisha! Boost your followers, likes & views now at bustisha.com',
  },
  {
    id: 'flash_sale',
    title: '⚡ 30% Flash Discount',
    badge: 'Hot',
    text: 'Exclusive Offer for {name}! Enjoy 30% OFF all Instagram, TikTok & YouTube services today only. Place your orders now at bustisha.com',
  },
  {
    id: 'welcome_back',
    title: '🚀 Welcome / Activation',
    badge: 'New',
    text: 'Hello {username}, your Bustisha account is ready! Deposit now starting from 1,000 TSHS and enjoy instant delivery at bustisha.com',
  },
  {
    id: 'weekend_special',
    title: '🌟 Weekend Promo',
    badge: 'Special',
    text: 'Weekend Promo! Top up your Bustisha balance today and get fast-track delivery on Telegram, Spotify & Facebook services: bustisha.com',
  },
];

export default function AdminOffers() {
  const { token } = useAuth();
  const [target, setTarget] = useState('all');
  const [message, setMessage] = useState('');
  const [customNumbers, setCustomNumbers] = useState('');
  const [audience, setAudience] = useState({
    textify_configured: false,
    segments: { all: 0, new_users: 0, active_buyers: 0, deposited: 0 },
  });
  const [loadingCounts, setLoadingCounts] = useState(true);
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [modal, setModal] = useState({ open: false, type: 'info', title: '', message: '' });

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const fetchAudience = () => {
    setLoadingCounts(true);
    fetch(`${API}/admin/offers/audience-counts`, { headers })
      .then((r) => r.json())
      .then((d) => {
        setAudience(d);
      })
      .catch(() => {})
      .finally(() => setLoadingCounts(false));
  };

  useEffect(() => {
    fetchAudience();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Character & SMS calculation
  const charLength = message.length;
  const smsSegments = charLength === 0 ? 0 : charLength <= 160 ? 1 : Math.ceil(charLength / 153);

  // Target count calculation
  const getTargetCount = () => {
    if (target === 'custom') {
      const phones = customNumbers
        .split(/[\r\n,;\s]+/)
        .map((p) => p.trim())
        .filter(Boolean);
      return phones.length;
    }
    return audience.segments?.[target] || 0;
  };

  const handleApplyTemplate = (tmplText) => {
    setMessage(tmplText);
  };

  const handleInsertTag = (tag) => {
    setMessage((prev) => (prev ? `${prev} ${tag}` : tag));
  };

  const handleOpenConfirm = (e) => {
    e.preventDefault();
    if (!audience.textify_configured) {
      setModal({
        open: true,
        type: 'error',
        title: 'SMS Not Configured',
        message: 'Please configure your Textify Africa API key in Admin Settings before sending promotion SMS.',
      });
      return;
    }
    if (!message.trim()) {
      setModal({
        open: true,
        type: 'error',
        title: 'Empty Message',
        message: 'Please write a promotional message before sending.',
      });
      return;
    }
    if (target === 'custom' && !customNumbers.trim()) {
      setModal({
        open: true,
        type: 'error',
        title: 'No Phone Numbers',
        message: 'Please enter at least one recipient phone number.',
      });
      return;
    }
    if (getTargetCount() === 0) {
      setModal({
        open: true,
        type: 'error',
        title: 'No Recipients',
        message: 'There are no recipients matching the selected audience segment.',
      });
      return;
    }
    setConfirmOpen(true);
  };

  const handleSendBroadcast = async () => {
    setConfirmOpen(false);
    setSending(true);

    try {
      const res = await fetch(`${API}/admin/offers/send-sms`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          target,
          message,
          custom_numbers: target === 'custom' ? customNumbers : undefined,
        }),
      });

      const data = await res.json();
      setSending(false);

      if (!res.ok) {
        setModal({
          open: true,
          type: 'error',
          title: 'Broadcast Failed',
          message: data.message || 'Failed to send SMS campaign.',
        });
        return;
      }

      setModal({
        open: true,
        type: 'success',
        title: 'Campaign Sent!',
        message: data.message || `Successfully sent ${data.sent} messages!`,
      });

      // Clear or keep as appropriate
      if (target === 'custom') setCustomNumbers('');
    } catch (err) {
      setSending(false);
      setModal({
        open: true,
        type: 'error',
        title: 'Broadcast Error',
        message: err.message || 'Network error while broadcasting SMS.',
      });
    }
  };

  // Preview text for phone simulator
  const previewText = message
    ? message.replace(/{username}/g, 'john_doe').replace(/{name}/g, 'John Doe')
    : 'Your promotional SMS message will appear here in real-time as you type...';

  return (
    <div className="admin-offers">
      <div className="offers-header">
        <div className="offers-header-text">
          <h1>
            <FontAwesomeIcon icon={faBullhorn} className="offers-header-icon" />
            Offers &amp; SMS Promotions
          </h1>
          <p>Broadcast promotional SMS, special discounts, and deposit bonus offers directly to your users' phones.</p>
        </div>
        <div className="offers-header-actions">
          {audience.textify_configured ? (
            <span className="offers-status-badge offers-status-active">
              ● Textify SMS Active
            </span>
          ) : (
            <Link to="/beast/settings" className="offers-status-badge offers-status-warning">
              ⚠️ Configure Textify API Key
            </Link>
          )}
        </div>
      </div>

      {!audience.textify_configured && (
        <div className="offers-alert-banner">
          <FontAwesomeIcon icon={faExclamationTriangle} className="offers-alert-icon" />
          <div className="offers-alert-body">
            <strong>Textify Africa SMS is not yet configured.</strong>
            <p>
              To broadcast SMS offers to your users, add your Textify Africa API Key in{' '}
              <Link to="/beast/settings">Admin Settings &rarr; SMS Notifications</Link>.
            </p>
          </div>
        </div>
      )}

      <div className="offers-grid">
        {/* ── Left Column: Configuration & Message ── */}
        <div className="offers-card offers-main-card">
          <form onSubmit={handleOpenConfirm}>
            {/* 1. Target Audience Selection */}
            <div className="offers-section">
              <label className="offers-section-title">
                <FontAwesomeIcon icon={faUsers} /> 1. Select Target Audience
              </label>

              <div className="audience-grid">
                <div
                  className={`audience-card ${target === 'all' ? 'selected' : ''}`}
                  onClick={() => setTarget('all')}
                >
                  <div className="audience-icon-wrap">
                    <FontAwesomeIcon icon={faUsers} />
                  </div>
                  <div className="audience-info">
                    <div className="audience-name">All Active Users</div>
                    <div className="audience-desc">All registered users with phone</div>
                  </div>
                  <div className="audience-count">
                    {loadingCounts ? '...' : `${audience.segments?.all || 0} users`}
                  </div>
                </div>

                <div
                  className={`audience-card ${target === 'new_users' ? 'selected' : ''}`}
                  onClick={() => setTarget('new_users')}
                >
                  <div className="audience-icon-wrap">
                    <FontAwesomeIcon icon={faUserPlus} />
                  </div>
                  <div className="audience-info">
                    <div className="audience-name">New Users (0 Orders)</div>
                    <div className="audience-desc">Registered but haven't ordered</div>
                  </div>
                  <div className="audience-count">
                    {loadingCounts ? '...' : `${audience.segments?.new_users || 0} users`}
                  </div>
                </div>

                <div
                  className={`audience-card ${target === 'active_buyers' ? 'selected' : ''}`}
                  onClick={() => setTarget('active_buyers')}
                >
                  <div className="audience-icon-wrap">
                    <FontAwesomeIcon icon={faShoppingBag} />
                  </div>
                  <div className="audience-info">
                    <div className="audience-name">Active Buyers</div>
                    <div className="audience-desc">Users with completed orders</div>
                  </div>
                  <div className="audience-count">
                    {loadingCounts ? '...' : `${audience.segments?.active_buyers || 0} users`}
                  </div>
                </div>

                <div
                  className={`audience-card ${target === 'deposited' ? 'selected' : ''}`}
                  onClick={() => setTarget('deposited')}
                >
                  <div className="audience-icon-wrap">
                    <FontAwesomeIcon icon={faWallet} />
                  </div>
                  <div className="audience-info">
                    <div className="audience-name">Deposited Users</div>
                    <div className="audience-desc">Users with positive balance</div>
                  </div>
                  <div className="audience-count">
                    {loadingCounts ? '...' : `${audience.segments?.deposited || 0} users`}
                  </div>
                </div>

                <div
                  className={`audience-card ${target === 'custom' ? 'selected' : ''}`}
                  onClick={() => setTarget('custom')}
                >
                  <div className="audience-icon-wrap">
                    <FontAwesomeIcon icon={faMobileAlt} />
                  </div>
                  <div className="audience-info">
                    <div className="audience-name">Custom Numbers</div>
                    <div className="audience-desc">Enter specific phone numbers</div>
                  </div>
                  <div className="audience-count">Manual list</div>
                </div>
              </div>

              {target === 'custom' && (
                <div className="custom-numbers-wrap">
                  <label className="offers-sublabel">Enter Phone Numbers (comma or newline separated)</label>
                  <textarea
                    className="custom-numbers-input"
                    rows={3}
                    placeholder="255712345678, 255787654321&#10;255655000111"
                    value={customNumbers}
                    onChange={(e) => setCustomNumbers(e.target.value)}
                    required
                  />
                  <span className="offers-subhint">Numbers must include country code without + sign (e.g. 255712345678).</span>
                </div>
              )}
            </div>

            {/* 2. Quick Templates */}
            <div className="offers-section">
              <label className="offers-section-title">
                <FontAwesomeIcon icon={faMagic} /> 2. Quick Templates (Click to load)
              </label>
              <div className="templates-row">
                {TEMPLATES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className="template-btn"
                    onClick={() => handleApplyTemplate(t.text)}
                  >
                    <span className="template-badge">{t.badge}</span>
                    <span className="template-title">{t.title}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* 3. Message Composition */}
            <div className="offers-section">
              <div className="offers-section-header">
                <label className="offers-section-title">
                  <FontAwesomeIcon icon={faCommentDots} /> 3. SMS Message
                </label>
                <div className="variable-tags">
                  <span className="tags-label">Insert variable:</span>
                  <button type="button" className="tag-btn" onClick={() => handleInsertTag('{username}')}>
                    {'{username}'}
                  </button>
                  <button type="button" className="tag-btn" onClick={() => handleInsertTag('{name}')}>
                    {'{name}'}
                  </button>
                </div>
              </div>

              <textarea
                className="offers-textarea"
                rows={5}
                placeholder="Write your promotional message here... e.g. Hello {username}, get 20% bonus on your next deposit on Bustisha!"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={500}
                required
              />

              <div className="textarea-footer">
                <div className="char-counter">
                  <strong>{charLength}</strong> / 500 characters &bull;{' '}
                  <span className={smsSegments > 1 ? 'sms-multi-warning' : 'sms-single'}>
                    {smsSegments} SMS {smsSegments > 1 ? 'segments' : 'segment'} per user
                  </span>
                </div>
                <div className="sender-tag">
                  Sender ID: <strong>BUSTISHA</strong>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="offers-submit-row">
              <button
                type="submit"
                className="offers-send-btn"
                disabled={sending || !audience.textify_configured}
              >
                {sending ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin /> Broadcasting SMS...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPaperPlane} /> Send Broadcast to {getTargetCount()} User(s)
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* ── Right Column: Live Phone Preview & Campaign Summary ── */}
        <div className="offers-card offers-preview-card">
          <h3 className="preview-title">
            <FontAwesomeIcon icon={faPhone} /> Live SMS Preview
          </h3>
          <p className="preview-subtitle">See how your message appears on users' phones.</p>

          <div className="phone-mockup">
            <div className="phone-notch" />
            <div className="phone-screen">
              <div className="phone-sms-header">
                <div className="phone-sender-avatar">B</div>
                <div className="phone-sender-info">
                  <div className="phone-sender-name">BUSTISHA</div>
                  <div className="phone-sender-sub">SMS &bull; Verified Sender</div>
                </div>
              </div>

              <div className="phone-messages-area">
                <div className="phone-bubble">
                  <p className="bubble-text">{previewText}</p>
                  <span className="bubble-time">Just now</span>
                </div>
              </div>

              <div className="phone-input-bar">
                <span>Text message (read-only)</span>
              </div>
            </div>
          </div>

          {/* Campaign summary card */}
          <div className="campaign-summary-card">
            <h4>Campaign Summary</h4>
            <div className="summary-row">
              <span>Target Audience:</span>
              <strong>
                {target === 'all' && 'All Active Users'}
                {target === 'new_users' && 'New Users (0 Orders)'}
                {target === 'active_buyers' && 'Active Buyers'}
                {target === 'deposited' && 'Deposited Users'}
                {target === 'custom' && 'Custom List'}
              </strong>
            </div>
            <div className="summary-row">
              <span>Recipients:</span>
              <strong className="highlight-number">{getTargetCount()} phones</strong>
            </div>
            <div className="summary-row">
              <span>SMS per Recipient:</span>
              <strong>{smsSegments} SMS</strong>
            </div>
            <div className="summary-row">
              <span>Est. Total SMS Credits:</span>
              <strong>{getTargetCount() * smsSegments} credits</strong>
            </div>
          </div>
        </div>
      </div>

      {/* ── Confirmation Modal ── */}
      {confirmOpen && (
        <div className="confirm-modal-overlay">
          <div className="confirm-modal-box">
            <div className="confirm-modal-header">
              <FontAwesomeIcon icon={faPaperPlane} className="confirm-modal-icon" />
              <h3>Confirm SMS Broadcast</h3>
            </div>
            <div className="confirm-modal-body">
              <p>
                You are about to broadcast an SMS to <strong>{getTargetCount()} recipient(s)</strong>.
              </p>
              <div className="confirm-summary-box">
                <div>
                  <strong>Audience:</strong> {target}
                </div>
                <div>
                  <strong>Total SMS:</strong> {getTargetCount() * smsSegments} credits ({smsSegments} SMS/user)
                </div>
                <div>
                  <strong>Sender ID:</strong> BUSTISHA
                </div>
                <div className="confirm-preview-snippet">
                  <strong>Message:</strong> "{previewText}"
                </div>
              </div>
              <p className="confirm-warning">
                <FontAwesomeIcon icon={faInfoCircle} /> Once sent, this message cannot be recalled and credits will be deducted from your Textify account.
              </p>
            </div>
            <div className="confirm-modal-actions">
              <button
                type="button"
                className="confirm-cancel-btn"
                onClick={() => setConfirmOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="confirm-proceed-btn"
                onClick={handleSendBroadcast}
              >
                <FontAwesomeIcon icon={faPaperPlane} /> Confirm &amp; Broadcast
              </button>
            </div>
          </div>
        </div>
      )}

      <Modal
        open={modal.open}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        onClose={() => setModal({ ...modal, open: false })}
      />
    </div>
  );
}
